import { P } from '@piccolohealth/util';

export const noop = async (): Promise<void> => {
  return;
};

export const map = async <A, B>(
  arr: A[],
  f: (value: A, index: number) => Promise<B>,
  options?: { concurrency: number },
): Promise<B[]> => {
  const concurrency = options?.concurrency ?? arr.length;
  const results: B[] = new Array(arr.length);
  let currentIndex = 0;

  const worker = async () => {
    while (currentIndex < arr.length) {
      const index = currentIndex++;
      results[index] = await f(arr[index]!, index);
    }
  };

  // Launch workers based on concurrency limit
  await Promise.all(Array.from({ length: Math.min(concurrency, arr.length) }, worker));

  return results;
};

export const each = async <A>(
  arr: A[],
  f: (value: A, index: number) => Promise<void>,
): Promise<void> => {
  await map(arr, f, { concurrency: 1 });
};

export const flatMap = async <A, B>(
  arr: A[],
  f: (value: A, index: number) => Promise<B[]>,
  options?: { concurrency: number },
) => {
  return map(arr, f, options).then((results) => results.flat());
};

export const allFlatten = async <A>(arr: Promise<A[]>[]): Promise<A[]> => {
  const res = await Promise.all(arr);
  return res.flat();
};

export const retry = async <A>(
  fn: () => Promise<A>,
  options: {
    attempts: number;
    delayMs: number;
    backoff?: 'fixed' | 'exponential';
    jitter?: boolean;
    isRetryable?: (error: unknown) => boolean;
    onRetry?: (attempt: { error: unknown; attempt: number; delayMs: number }) => void;
  },
): Promise<A> => {
  const go = async (attempt: number): Promise<A> => {
    try {
      return await fn();
    } catch (error) {
      const retryable = options.isRetryable ?? P.constant(true);

      if (attempt >= options.attempts || !retryable(error)) {
        throw error;
      }

      const backoff = options.backoff === 'exponential' ? 2 ** (attempt - 1) : 1;
      // Jitter, so callers throttled together don't all retry in lockstep
      const jitter = options.jitter ? 1 + Math.random() : 1;
      const delayMs = options.delayMs * backoff * jitter;

      options.onRetry?.({ error, attempt, delayMs });

      await delay(delayMs);

      return go(attempt + 1);
    }
  };

  return go(1);
};

export const some = async <A>(arr: A[], f: (value: A) => Promise<boolean>): Promise<boolean> => {
  for (const item of arr) {
    const ret = await f(item);
    if (ret) {
      return ret;
    }
  }

  return false;
};

export const find = async <A>(arr: A[], f: (value: A) => Promise<boolean>): Promise<A | null> => {
  for (const item of arr) {
    const ret = await f(item);
    if (ret) {
      return item;
    }
  }

  return null;
};

export const reduce = async <T, R>(
  arr: T[],
  f: (accumulator: R, current: T, index: number, length: number) => Promise<R>,
  initial: R,
): Promise<R> => {
  let updatedInitial = initial;

  if (arr.length === 0) {
    return updatedInitial;
  }

  for (const [index, value] of arr.entries()) {
    updatedInitial = await f(updatedInitial, value, index, arr.length);
  }

  return updatedInitial;
};

export const delay = (milliseconds: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(() => resolve(), milliseconds);
  });
};

export const props = async <A extends object>(
  obj: A,
): Promise<{ [P in keyof A]: Awaited<A[P]> }> => {
  const awaiters: Promise<void>[] = [];
  const ret = {} as { [P in keyof A]: Awaited<A[P]> };

  for (const key in obj) {
    awaiters.push(
      Promise.resolve(obj[key]).then((val) => {
        ret[key] = val;
      }),
    );
  }

  await Promise.all(awaiters);
  return ret;
};

export const allSettled = <A>(promises: Promise<A>[]) => {
  return Promise.all(
    promises.map((promise) =>
      promise
        .then((value) => ({
          status: 'fulfilled',
          value,
        }))
        .catch((reason) => ({
          status: 'rejected',
          reason,
        })),
    ),
  );
};

export const pollUntil = async <A>(
  fn: () => Promise<A>,
  condition: (result: A) => boolean,
  delayMs: number,
  maxAttempts = 30,
): Promise<A> => {
  const go = async (
    fn: () => Promise<A>,
    condition: (result: A) => boolean,
    delayMs: number,
    attempts: number,
  ): Promise<A> => {
    const result = await fn();

    if (condition(result)) {
      return result;
    } else if (attempts === maxAttempts) {
      return Promise.reject(new Error('Too many attempts'));
    } else {
      await delay(delayMs);
      return go(fn, condition, delayMs, attempts + 1);
    }
  };

  return go(fn, condition, delayMs, 1);
};

export const timeout = <A>(ms: number, promise: Promise<A>): Promise<A> => {
  return new Promise<A>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`Timeout after ${ms}ms`));
    }, ms);

    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
};
